import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    AddCircleOutline,
    Clear,
    CloudDownloadOutlined,
    EditOutlined,
    Launch,
    Refresh,
    Search,
    Sync,
} from "@mui/icons-material";
import { getComparator, stableSort } from "./pluginTableUtils";

const getPluginName = (plugin) => plugin.plugin_name ?? plugin.name ?? `Плагин #${plugin.plugin_id}`;
const getProviderName = (plugin) => plugin.provider ?? plugin.provider_name ?? plugin.source ?? `Источник ${plugin.provider_id ?? '—'}`;
const getUpdatedAt = (plugin) => plugin.updated_at ?? plugin.update_date ?? plugin.last_update ?? null;
const isPlayerVisible = (plugin) => Boolean(plugin.visible ?? plugin.is_visible ?? plugin.player_visible ?? true);

const formatDate = (value) => {
    if (!value) return 'Нет данных';
    const date = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const headCells = [
    { id: 'name', label: 'Плагин' },
    { id: 'provider', label: 'Источник' },
    { id: 'version', label: 'Версия' },
    { id: 'updated_at', label: 'Обновлён' },
    { id: 'status', label: 'Активен' },
    { id: 'visible', label: 'Игрокам' },
];

const StatCard = ({ label, value }) => (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 120, flex: '1 1 120px' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={700}>{value}</Typography>
    </Paper>
);

const PluginsTable = ({
    plugins,
    stats,
    loading,
    error,
    updatingPluginIds,
    onReload,
    onAddPlugin,
    onOpenDetails,
    onToggleStatus,
    onToggleVisibility,
    onOpenSettings,
    onUpdatePlugin,
    onUpdateVisible,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [search, setSearch] = useState('');
    const [provider, setProvider] = useState('all');
    const [status, setStatus] = useState('all');
    const [visibility, setVisibility] = useState('all');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [selectedIds, setSelectedIds] = useState([]);

    const providers = useMemo(() => (
        [...new Set(plugins.map(getProviderName).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ru'))
    ), [plugins]);

    const filteredPlugins = useMemo(() => {
        const query = search.trim().toLocaleLowerCase('ru');

        const filtered = plugins.filter((plugin) => {
            const pluginName = getPluginName(plugin).toLocaleLowerCase('ru');
            const providerName = getProviderName(plugin);
            const version = String(plugin.version ?? '').toLocaleLowerCase('ru');
            const matchesSearch = !query || pluginName.includes(query) || providerName.toLocaleLowerCase('ru').includes(query) || version.includes(query);
            const matchesProvider = provider === 'all' || providerName === provider;
            const matchesStatus = status === 'all' || (status === 'enabled' ? Boolean(plugin.status) : !Boolean(plugin.status));
            const visible = isPlayerVisible(plugin);
            const matchesVisibility = visibility === 'all' || (visibility === 'players' ? visible : !visible);

            return matchesSearch && matchesProvider && matchesStatus && matchesVisibility;
        });

        return stableSort(filtered, getComparator(order, orderBy));
    }, [plugins, search, provider, status, visibility, order, orderBy]);

    const visibleIds = filteredPlugins.map(plugin => plugin.plugin_id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

    const resetFilters = () => {
        setSearch('');
        setProvider('all');
        setStatus('all');
        setVisibility('all');
    };

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...visibleIds])]);
        }
    };

    const toggleSelected = (pluginId) => {
        setSelectedIds(prev => prev.includes(pluginId) ? prev.filter(id => id !== pluginId) : [...prev, pluginId]);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const updateTargetIds = selectedIds.length > 0 ? selectedIds : visibleIds;

    return (
        <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Библиотека плагинов</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Все плагины на одной странице: поиск, фильтры, загрузка и обновление без переходов между страницами.
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" startIcon={<Refresh />} onClick={onReload}>Обновить список</Button>
                        <Button variant="contained" startIcon={<AddCircleOutline />} onClick={onAddPlugin}>Добавить плагин</Button>
                    </Stack>
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2.5 }}>
                    <StatCard label="Всего" value={stats.total} />
                    <StatCard label="Активны" value={stats.enabled} />
                    <StatCard label="Выключены" value={stats.disabled} />
                    <StatCard label="Игрокам" value={stats.visible ?? 0} />
                    <StatCard label="Админские" value={stats.hidden ?? 0} />
                    <StatCard label="Источников" value={stats.providers} />
                </Stack>
            </Paper>

            <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2, position: 'sticky', top: 0, zIndex: 5 }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Поиск по названию, источнику или версии"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')}><Clear fontSize="small" /></IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{ minWidth: { lg: 320 } }}
                    />

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 210 } }}>
                        <InputLabel>Источник</InputLabel>
                        <Select value={provider} label="Источник" onChange={(event) => setProvider(event.target.value)}>
                            <MenuItem value="all">Все источники</MenuItem>
                            {providers.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                        <InputLabel>Статус</InputLabel>
                        <Select value={status} label="Статус" onChange={(event) => setStatus(event.target.value)}>
                            <MenuItem value="all">Все</MenuItem>
                            <MenuItem value="enabled">Активные</MenuItem>
                            <MenuItem value="disabled">Выключенные</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
                        <InputLabel>Видимость</InputLabel>
                        <Select value={visibility} label="Видимость" onChange={(event) => setVisibility(event.target.value)}>
                            <MenuItem value="all">Все плагины</MenuItem>
                            <MenuItem value="players">Только для игроков</MenuItem>
                            <MenuItem value="admin">Только админские</MenuItem>
                        </Select>
                    </FormControl>

                    <Button variant="text" onClick={resetFilters}>Сбросить</Button>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Показано: <strong>{filteredPlugins.length}</strong> из {plugins.length}
                        {selectedIds.length > 0 ? ` · выбрано: ${selectedIds.length}` : ''}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Sync />}
                        disabled={!updateTargetIds.length || updatingPluginIds.length > 0}
                        onClick={() => onUpdateVisible(updateTargetIds)}
                    >
                        {selectedIds.length > 0 ? 'Обновить выбранные' : 'Обновить видимые'}
                    </Button>
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Не удалось загрузить список плагинов.</Alert>}

            {loading && !plugins.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : filteredPlugins.length === 0 ? (
                <Paper sx={{ p: 5, textAlign: 'center' }}>
                    <Typography variant="h6">Плагины не найдены</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>Измени фильтры или поисковый запрос.</Typography>
                </Paper>
            ) : isMobile ? (
                <Stack spacing={1.25}>
                    {filteredPlugins.map((plugin) => {
                        const updating = updatingPluginIds.includes(plugin.plugin_id);
                        return (
                            <Card key={plugin.plugin_id} variant="outlined">
                                <CardContent sx={{ pb: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={700}
                                                noWrap
                                                onClick={() => onOpenDetails(plugin.plugin_id)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {getPluginName(plugin)}
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                                                <Chip size="small" label={getProviderName(plugin)} />
                                                <Chip size="small" variant="outlined" label={`v${plugin.version ?? '—'}`} />
                                                <Chip size="small" color={plugin.status ? 'success' : 'default'} label={plugin.status ? 'Активен' : 'Выключен'} />
                                                <Chip size="small" variant="outlined" color={isPlayerVisible(plugin) ? 'primary' : 'default'} label={isPlayerVisible(plugin) ? 'Игрокам' : 'Админский'} />
                                            </Stack>
                                        </Box>
                                        <Checkbox checked={selectedIds.includes(plugin.plugin_id)} onChange={() => toggleSelected(plugin.plugin_id)} />
                                    </Stack>

                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
                                        Обновлён: {formatDate(getUpdatedAt(plugin))}
                                    </Typography>

                                    {plugin.download_url && (
                                        <Link href={plugin.download_url} target="_blank" rel="noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                                            Открыть источник <Launch fontSize="inherit" />
                                        </Link>
                                    )}
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
                                    <Stack spacing={0.25}>
                                        <Stack direction="row" alignItems="center">
                                            <Switch size="small" checked={Boolean(plugin.status)} onChange={() => onToggleStatus(!plugin.status, plugin.plugin_id)} />
                                            <Typography variant="body2">{plugin.status ? 'Включён' : 'Выключен'}</Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center">
                                            <Switch size="small" checked={isPlayerVisible(plugin)} onChange={() => onToggleVisibility(!isPlayerVisible(plugin), plugin.plugin_id)} />
                                            <Typography variant="body2">{isPlayerVisible(plugin) ? 'Виден игрокам' : 'Админский'}</Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row">
                                        <Tooltip title="Настройки"><IconButton onClick={() => onOpenSettings(plugin)}><EditOutlined /></IconButton></Tooltip>
                                        <Tooltip title="Обновить">
                                            <span>
                                                <IconButton disabled={updating} onClick={() => onUpdatePlugin(plugin.plugin_id)}>
                                                    {updating ? <CircularProgress size={20} /> : <CloudDownloadOutlined />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </CardActions>
                            </Card>
                        );
                    })}
                </Stack>
            ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 220px)' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={allVisibleSelected} indeterminate={!allVisibleSelected && selectedIds.some(id => visibleIds.includes(id))} onChange={toggleAllVisible} />
                                </TableCell>
                                {headCells.map((cell) => (
                                    <TableCell key={cell.id} sortDirection={orderBy === cell.id ? order : false}>
                                        <TableSortLabel active={orderBy === cell.id} direction={orderBy === cell.id ? order : 'asc'} onClick={() => handleRequestSort(cell.id)}>
                                            {cell.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPlugins.map((plugin) => {
                                const updating = updatingPluginIds.includes(plugin.plugin_id);
                                return (
                                    <TableRow hover key={plugin.plugin_id} selected={selectedIds.includes(plugin.plugin_id)}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedIds.includes(plugin.plugin_id)} onChange={() => toggleSelected(plugin.plugin_id)} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight={600} sx={{ cursor: 'pointer' }} onClick={() => onOpenDetails(plugin.plugin_id)}>
                                                {getPluginName(plugin)}
                                            </Typography>
                                            {plugin.download_url && (
                                                <Link href={plugin.download_url} target="_blank" rel="noreferrer" variant="caption" underline="hover">
                                                    Открыть источник
                                                </Link>
                                            )}
                                        </TableCell>
                                        <TableCell><Chip size="small" label={getProviderName(plugin)} /></TableCell>
                                        <TableCell>{plugin.version ?? '—'}</TableCell>
                                        <TableCell>{formatDate(getUpdatedAt(plugin))}</TableCell>
                                        <TableCell>
                                            <Switch size="small" checked={Boolean(plugin.status)} onChange={() => onToggleStatus(!plugin.status, plugin.plugin_id)} />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={isPlayerVisible(plugin) ? 'Виден игрокам' : 'Скрыт от игроков'}>
                                                <Switch size="small" checked={isPlayerVisible(plugin)} onChange={() => onToggleVisibility(!isPlayerVisible(plugin), plugin.plugin_id)} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Настройки"><IconButton size="small" onClick={() => onOpenSettings(plugin)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Обновить">
                                                <span>
                                                    <IconButton size="small" disabled={updating} onClick={() => onUpdatePlugin(plugin.plugin_id)}>
                                                        {updating ? <CircularProgress size={18} /> : <CloudDownloadOutlined fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default PluginsTable;
